import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Box
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

export default function DepartmentFormModal({ open, onClose, onSuccess, departmentData, staffMembers }) {
    const isEditMode = !!departmentData;
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        headOfDepartmentId: '',
        costCenterCode: '',
        displayOrder: 1
    });

    const [staffList, setStaffList] = useState([]);
    const [isLoadingStaff, setIsLoadingStaff] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            fetchStaff();
            if (departmentData) {
                setFormData({
                    name: departmentData.name || '',
                    code: departmentData.code || '',
                    description: departmentData.description || '',
                    headOfDepartmentId: departmentData.headOfDepartmentId || departmentData.headOfDepartment?.id || '',
                    costCenterCode: departmentData.costCenterCode || '',
                    displayOrder: departmentData.displayOrder || 1
                });
            } else {
                setFormData({
                    name: '',
                    code: '',
                    description: '',
                    headOfDepartmentId: '',
                    costCenterCode: '',
                    displayOrder: 1
                });
            }
            setError('');
        }
    }, [open, departmentData]);

    const fetchStaff = async () => {
        if (staffMembers && Array.isArray(staffMembers) && staffMembers.length > 0) {
            setStaffList(staffMembers);
            return;
        }
        setIsLoadingStaff(true);
        try {
            const data = await StaffService.getStaff({ noPagination: true });
            const list = Array.isArray(data) ? data : (data?.data || []);
            setStaffList(list);
        } catch (err) {
            console.error('Failed to fetch staff members:', err);
        } finally {
            setIsLoadingStaff(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (!formData.name || !formData.code || !formData.costCenterCode) {
                throw new Error('Name, code, and cost center code are required.');
            }

            if (isEditMode) {
                const updates = {};
                if (formData.name !== departmentData.name) updates.name = formData.name;
                if (formData.code !== departmentData.code) updates.code = formData.code;
                if (formData.description !== departmentData.description) updates.description = formData.description;
                if (formData.headOfDepartmentId !== (departmentData.headOfDepartmentId || departmentData.headOfDepartment?.id || '')) updates.headOfDepartmentId = formData.headOfDepartmentId;
                if (formData.costCenterCode !== departmentData.costCenterCode) updates.costCenterCode = formData.costCenterCode;
                if (formData.displayOrder !== departmentData.displayOrder) updates.displayOrder = parseInt(formData.displayOrder, 10);

                // Prevent sending empty string if not selected
                if (updates.headOfDepartmentId === '') delete updates.headOfDepartmentId;
                if (updates.description === '') delete updates.description;

                if (Object.keys(updates).length === 0) {
                    onClose();
                    return;
                }

                await StaffService.updateDepartment(departmentData.id || departmentData._id, updates);
                onSuccess('Department updated successfully');
            } else {
                const payload = {
                    ...formData,
                    displayOrder: parseInt(formData.displayOrder, 10) || 1
                };

                // Remove empty strings so they are not passed
                if (!payload.headOfDepartmentId) delete payload.headOfDepartmentId;
                if (!payload.description) delete payload.description;

                await StaffService.createDepartment(payload);
                onSuccess('Department created successfully');
            }
            
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create department.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>{isEditMode ? 'Edit Department' : 'Add New Department'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Department Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="Housekeeping"
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Code"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="HK"
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Cost Center Code"
                                name="costCenterCode"
                                value={formData.costCenterCode}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="CC-HK-001"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Display Order"
                                name="displayOrder"
                                type="number"
                                value={formData.displayOrder}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                inputProps={{ min: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Head of Department</InputLabel>
                                <Select
                                    name="headOfDepartmentId"
                                    value={formData.headOfDepartmentId}
                                    onChange={handleChange}
                                    label="Head of Department"
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {isLoadingStaff ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading staff...</MenuItem>
                                    ) : staffList.length > 0 ? (
                                        staffList.map(staff => {
                                            const name = staff.fullName || staff.name || `${staff.firstName || ''} ${staff.lastName || ''}`.trim() || 'Staff';
                                            const idInfo = staff.employeeId ? ` - ${staff.employeeId}` : (staff.designation ? ` (${staff.designation})` : '');
                                            return (
                                                <MenuItem key={staff.id || staff._id} value={staff.id || staff._id}>
                                                    {name}{idInfo}
                                                </MenuItem>
                                            );
                                        })
                                    ) : (
                                        <MenuItem disabled><em>No staff members available</em></MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                placeholder="Room and public area cleaning"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                    <Button onClick={onClose} color="inherit">Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} />}
                        sx={{ px: 4 }}
                    >
                        {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Department'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
